
/**
 * QuoteStore manages the "quote cart" in localStorage.
 * It provides methods to get, set, add, remove, update, clear items,
 * and calculate the total quote.
 */
class QuoteStore {
    /** @private */
    static STORAGE_KEY = 'quoteItems';

    /**
     * Retrieves the current quote items from localStorage.
     * @returns {Array<Object>} Array of quote items.
     */
    static getItems() {
        try {
            const raw = localStorage.getItem(QuoteStore.STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch (error) {
            console.error('Error parsing quoteItems from localStorage:', error);
            return [];
        }
    }

    /**
     * Saves the given items array to localStorage.
     * @param {Array<Object>} items - Array of quote items.
     */
    static setItems(items) {
        localStorage.setItem(QuoteStore.STORAGE_KEY, JSON.stringify(items));
    }

    /**
     * Adds a product to the quote cart.
     * If the product (matching variantId) already exists, updates its quantity and total.
     * Otherwise, adds the new product.
     *
     * @param {Object} product - The product object to add.
     *   Expected properties: variantId, price, qty, and optionally variantTitle, variantOptions, etc.
     */
    static addItem(product) {
        const items = QuoteStore.getItems();
        const existing = items.find(i => i.variantId === product.variantId);
        if (existing) {
            existing.qty += product.qty;
            existing.total = existing.price * existing.qty;
        } else {
            product.total = product.price * product.qty;
            items.push(product);
        }
        QuoteStore.setItems(items);
    }

    /**
     * Removes an item from the quote cart using its variant ID.
     * @param {string|number} variantId - The variant ID of the product to remove.
     */
    static removeItem(variantId) {
        const items = QuoteStore.getItems().filter(item => item.variantId !== variantId);
        QuoteStore.setItems(items);
    }

    /**
     * Updates the quantity of a product in the quote cart and recalculates its total.
     * @param {string|number} variantId - The variant ID of the product.
     * @param {number} newQty - The new quantity.
     */
    static updateQuantity(variantId, newQty) {
        const items = QuoteStore.getItems();
        const item = items.find(i => i.variantId === variantId);
        if (item) {
            item.qty = newQty;
            item.total = item.price * item.qty;
        }
        QuoteStore.setItems(items);
    }

    /**
     * Clears all quote items from localStorage.
     */
    static clearItems() {
        localStorage.removeItem(QuoteStore.STORAGE_KEY);
    }

    /**
     * Calculates the total price of all quote items.
     * @returns {number} The total quote value.
     */
    static getTotal() {
        const items = QuoteStore.getItems();
        return items.reduce((acc, item) => acc + item.total, 0);
    }
}

export default QuoteStore;
